import { useEffect, useMemo, useState } from 'react';
import { FaFileAlt, FaArchive } from 'react-icons/fa';
import { FiXCircle } from 'react-icons/fi';

import * as chkAuditoriaUtils from '../../../Services/Auditoria/chkAuditoriaUtils';
import requestPreSignedUrlAws from '../../../Services/requestPreSignedUrlAws';
import { useOperador } from '../../../Hooks/useOperador';
import { renderRespostaPorTipo } from './renderRespostaPorTipo';

import './cardQuestao.css';

const CardQuestao = ({ questao }) => {
  const [loading, setLoading] = useState(false);
  const [respostas, setRespostas] = useState([]);
  const [objeto, setObjeto] = useState(null);
  const { permissaoPrincipal } = useOperador();

  // =========================
  // CARREGAMENTO DE DADOS
  // =========================

  useEffect(() => {
    if (!questao) return;

    setLoading(true);

    Promise.all([
      chkAuditoriaUtils.getRespostasQuestao(questao.IDQuestao_Secao),
      questao.IDObjeto
        ? chkAuditoriaUtils.getObjetoQuestao(questao.IDObjeto)
        : Promise.resolve(null),
    ])
      .then(([respostasData, objetoData]) => {
        setRespostas(respostasData || []);
        setObjeto(objetoData);
      })
      .finally(() => setLoading(false));
  }, [questao]);

  const ultimaAtualizacao = useMemo(() => {
    if (!Array.isArray(respostas) || !respostas.length) return '';
    let melhor = null;
    respostas.forEach((item) => {
      const raw = item?.dataUltimaRespostaRaw;
      if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
        if (!melhor || raw > melhor.raw) {
          melhor = { raw, text: item?.dataUltimaResposta || '' };
        }
      }
    });
    return melhor?.text || '';
  }, [respostas]);

  const getNomeArquivo = (path = '') => {
    if (!path) return '';
    const partes = String(path).split('/');
    return partes[partes.length - 1] || '';
  };

  const abrirArquivo = async () => {
    if (!objeto) return;

    const bucket = objeto.nomeBucketAws || objeto.bucket || null;
    const caminho = objeto.caminho || null;
    const nome = objeto.descricao || getNomeArquivo(objeto.path);

    if (!bucket || !caminho) {
      alert('Não foi possível obter o link do arquivo.');
      return;
    }

    const urlArquivo = await requestPreSignedUrlAws(
      permissaoPrincipal,
      'getObject',
      bucket,
      caminho,
      nome,
      'inline',
    );

    if (!urlArquivo || typeof urlArquivo !== 'string') {
      alert('Não foi possível obter o link do arquivo.');
      return;
    }

    window.open(urlArquivo, '_blank');
  };

  // =========================
  // RENDER
  // =========================
  return (
    <div className="accQ-item open">
      {/* ===== CABEÇALHO ===== */}
      <div className="accQ-header">
        <div className="d-flex flex-column">
          <div className="accQ-left">
            <strong className="accQ-title">
              #{questao.IDQuestao} - {questao.titulo}
            </strong>
          </div>
          <div className="accQ-description-wrapper">
            <span className="accQ-description">{questao.descricao}</span>
          </div>
        </div>
        {questao?.situacao === 0 && (
          <div className="accQ-right">
            <span className="accQ-status-badge is-inativo" title="Questão inativa">
              <FiXCircle className="accQ-status-icon" />
              Inativo
            </span>
          </div>
        )}
      </div>

      {/* ===== OBJETO ===== */}
      <div className="accQ-wrapper">
        <div className="accQ-objeto">
          <div className="icon-processo">
            <FaArchive className="accQ-icon" />
          </div>
          <div className="accQ-label-objeto">
            {objeto ? objeto.processo : `Nenhum arquivo vinculado.`}
          </div>
          {objeto && (
            <div className="d-flex">
              <div className="icon-file">
                <FaFileAlt className="accQ-icon" />
              </div>
              <span
                className="accQ-label-objeto accQ-label-objeto--clickable"
                onClick={abrirArquivo}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    abrirArquivo();
                  }
                }}
                title="Abrir arquivo"
              >
                {objeto?.tipo === 2 ? objeto?.descricao : objeto?.path}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ===== RESPOSTAS ===== */}
      <div className="accQ-wrapper">
        <div className="accQ-content">
          <div className="accQ-layout">
            <div>
              {loading && (
                <p className="loading-text">Carregando resposta...</p>
              )}
              {!loading && renderRespostaPorTipo(questao.tipo, respostas)}
            </div>
          </div>
          {!loading && ultimaAtualizacao && (
            <div className="chk-resposta-info">
              <small>Última atualização: {ultimaAtualizacao}</small>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CardQuestao;
